/**
 * Hooks admin para catálogo de equipamiento (multi-distribuidor).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import { useAuth } from '@/hooks/useAuth';
import type {
    CatalogBatchImportItem,
    CatalogBatchResult,
    CatalogContentPatch,
    CatalogFichasStats,
    CatalogProductDetail,
    CatalogProductRow,
    CatalogSupplierRow,
    CatalogAllowlistSavedItem,
    CatalogRemoteListResult,
    CatalogSyncRunRow,
} from '../types';
import { catalogSyncStaleMaxHours, isStaleSync } from '../catalogSyncFreshness';
import { refreshStoreAfter } from '../../storeRefresh';

export { catalogSyncStaleMaxHours, isStaleSync } from '../catalogSyncFreshness';

const QK_PRODUCTS = ['admin', 'store-catalog-products'] as const;
const QK_SUPPLIERS = ['admin', 'store-catalog-suppliers'] as const;
const QK_RUNS = ['admin', 'store-catalog-sync-runs'] as const;
const QK_SUPPLIER_ACTIVITY = ['admin', 'store-supplier-activity'] as const;
const QK_DETAIL = ['admin', 'store-catalog-product'] as const;
const QK_ALLOWLIST = ['admin', 'store-catalog-allowlist'] as const;

const QK_FICHAS_STATS = ['admin', 'store-catalog-fichas-stats'] as const;

function asPayload<T>(data: unknown): T {
    const norm = normalizeRpcResponse(data);
    return (norm[0] ?? {}) as T;
}

export function useSetSupplierActive() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { slug: string; isActive: boolean }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_set_catalog_supplier_active', {
                p_admin_user_id: user.id,
                p_supplier_slug: input.slug,
                p_is_active: input.isActive,
            });
            if (error) throw new Error(error.message);
            return asPayload<{ slug?: string; isActive?: boolean }>(data);
        },
        onSuccess: () => refreshStoreAfter(qc, 'supplier-save'),
    });
}

export interface SupplierActivityItem {
    id: number;
    action: string;
    createdAt: string;
    actorName: string;
}

export function useSupplierActivity(slug: string) {
    const { user } = useAuth();
    return useQuery<SupplierActivityItem[]>({
        queryKey: [...QK_SUPPLIER_ACTIVITY, slug],
        enabled: Boolean(user?.id && slug),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_supplier_activity', {
                p_admin_user_id: user!.id,
                p_supplier_slug: slug,
                p_limit: 20,
            });
            if (error) throw new Error(error.message);
            const payload = asPayload<{ items?: SupplierActivityItem[] }>(data);
            return payload.items ?? [];
        },
        staleTime: 10_000,
    });
}

export function useUpdateSupplierConditions() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: {
            slug: string;
            orderContactEmail: string;
            leadTimeDays: number | null;
            deliveryDaysMin: number;
            deliveryDaysMax: number;
        }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_update_supplier_conditions', {
                p_admin_user_id: user.id,
                p_supplier_slug: input.slug,
                p_order_contact_email: input.orderContactEmail,
                p_lead_time_days: input.leadTimeDays,
                p_delivery_days_min: input.deliveryDaysMin,
                p_delivery_days_max: input.deliveryDaysMax,
            });
            if (error) throw new Error(error.message);
            return asPayload<Record<string, unknown>>(data);
        },
        onSuccess: () => refreshStoreAfter(qc, 'supplier-save'),
    });
}

export function useCatalogSuppliers() {
    const { user } = useAuth();
    return useQuery<CatalogSupplierRow[]>({
        queryKey: QK_SUPPLIERS,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_catalog_suppliers', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const payload = asPayload<{ items?: CatalogSupplierRow[] }>(data);
            return payload.items ?? [];
        },
        staleTime: 30_000,
    });
}

export interface CatalogProductsFilters {
    search?: string;
    supplier?: string;
    availability?: string;
    activeOnly?: boolean;
    qualityStatus?: string;
    incompleteOnly?: boolean;
    sort?: 'fichas';
    limit?: number;
    offset?: number;
}

export function useCatalogProducts(filters: CatalogProductsFilters) {
    const { user } = useAuth();
    return useQuery<{ items: CatalogProductRow[]; total: number }>({
        queryKey: [...QK_PRODUCTS, filters],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_catalog_products', {
                p_admin_user_id: user!.id,
                p_search: filters.search || null,
                p_supplier: filters.supplier || null,
                p_availability: filters.availability || null,
                p_active_only: filters.activeOnly ?? false,
                p_limit: filters.limit ?? 50,
                p_offset: filters.offset ?? 0,
                p_quality_status: filters.qualityStatus || null,
                p_incomplete_only: filters.incompleteOnly ?? false,
                p_sort: filters.sort || null,
            });
            if (error) throw new Error(error.message);
            const payload = asPayload<{ items?: CatalogProductRow[]; total?: number }>(data);
            return { items: payload.items ?? [], total: payload.total ?? 0 };
        },
        staleTime: 15_000,
    });
}

export function useCatalogProductDetail(productId: number | null) {
    const { user } = useAuth();
    return useQuery<CatalogProductDetail | null>({
        queryKey: [...QK_DETAIL, productId],
        enabled: Boolean(user?.id && productId != null),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_catalog_product', {
                p_admin_user_id: user!.id,
                p_product_id: productId,
            });
            if (error) throw new Error(error.message);
            if (data == null) return null;
            const norm = normalizeRpcResponse(data);
            return (norm[0] ?? null) as CatalogProductDetail | null;
        },
    });
}

export function useCatalogSyncRuns(supplier?: string) {
    const { user } = useAuth();
    return useQuery<CatalogSyncRunRow[]>({
        queryKey: [...QK_RUNS, supplier ?? 'all'],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_catalog_sync_runs', {
                p_admin_user_id: user!.id,
                p_supplier: supplier || null,
                p_limit: 20,
            });
            if (error) throw new Error(error.message);
            const payload = asPayload<{ items?: CatalogSyncRunRow[] }>(data);
            return payload.items ?? [];
        },
        staleTime: 10_000,
        refetchInterval: (query) => {
            const rows = query.state.data ?? [];
            const open = rows.some((row) => row.status === 'queued' || row.status === 'running');
            return open ? 2500 : false;
        },
    });
}

export function useSetCatalogPriceOverride() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { productId: number; precio?: number; unlock?: boolean }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_set_catalog_price_override', {
                p_admin_user_id: user.id,
                p_product_id: input.productId,
                p_precio: input.unlock ? null : input.precio,
                p_unlock: Boolean(input.unlock),
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data)[0] ?? null;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK_PRODUCTS });
            qc.invalidateQueries({ queryKey: QK_DETAIL });
        },
    });
}

export function useSetCatalogProductActive() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { productId: number; isActive: boolean }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_set_catalog_product_active', {
                p_admin_user_id: user.id,
                p_product_id: input.productId,
                p_is_active: input.isActive,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data)[0] ?? null;
        },
        onSuccess: () => refreshStoreAfter(qc, 'product-save'),
    });
}

export interface CatalogAvailabilityPatch {
    disponibilidad_manual?: string | null;
    lead_time_days_manual?: number | null;
    stock_qty_manual?: number | null;
    stock_enforced?: boolean;
    variants?: Array<{
        id: number;
        disponibilidad_manual?: string | null;
        lead_time_days_manual?: number | null;
        stock_qty_manual?: number | null;
        stock_enforced?: boolean;
    }>;
}

export function useUpdateCatalogAvailability() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { productId: number; patch: CatalogAvailabilityPatch }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_update_catalog_availability', {
                p_admin_user_id: user.id,
                p_product_id: input.productId,
                p_patch: input.patch,
            });
            if (error) throw new Error(error.message);
            return asPayload<CatalogProductDetail>(data);
        },
        onSuccess: () => refreshStoreAfter(qc, 'product-save'),
    });
}

export function useBatchSetCatalogAvailability() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { catalogIds: string[]; disponibilidad: string; leadTimeDays: number | null }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_batch_set_catalog_availability', {
                p_admin_user_id: user.id,
                p_catalog_ids: input.catalogIds.join(','),
                p_disponibilidad: input.disponibilidad,
                p_lead_time_days: input.leadTimeDays,
            });
            if (error) throw new Error(error.message);
            return asPayload<{ catalogUpdated?: number }>(data);
        },
        onSuccess: () => refreshStoreAfter(qc, 'product-batch'),
    });
}

export function useUpdateCatalogProductContent() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { productId: number; patch: CatalogContentPatch }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_update_catalog_product_content', {
                p_admin_user_id: user.id,
                p_product_id: input.productId,
                p_patch: input.patch,
            });
            if (error) throw new Error(error.message);
            if (input.patch.categoria_ruta != null || input.patch.coleccion_slugs != null) {
                const taxonomy = await dbClient.rpc('admin_update_catalog_taxonomy', {
                    p_admin_user_id: user.id,
                    p_product_id: input.productId,
                    p_patch: {
                        ...(input.patch.categoria_ruta != null
                            ? { categoria_ruta: input.patch.categoria_ruta }
                            : {}),
                        ...(input.patch.coleccion_slugs != null
                            ? { coleccion_slugs: input.patch.coleccion_slugs }
                            : {}),
                    },
                });
                if (taxonomy.error) throw new Error(taxonomy.error.message);
            }
            return normalizeRpcResponse(data)[0] ?? null;
        },
        onSuccess: () => refreshStoreAfter(qc, 'product-save'),
    });
}

export function useCatalogFichasStats(filters: { supplier?: string; activeOnly?: boolean }) {
    const { user } = useAuth();
    return useQuery<CatalogFichasStats>({
        queryKey: [...QK_FICHAS_STATS, filters],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_catalog_fichas_stats', {
                p_admin_user_id: user!.id,
                p_supplier: filters.supplier || null,
                p_active_only: filters.activeOnly ?? true,
            });
            if (error) throw new Error(error.message);
            return asPayload<CatalogFichasStats>(data);
        },
        staleTime: 15_000,
    });
}

export function useApplyCatalogProductContentBatch() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { items: CatalogBatchImportItem[]; dryRun: boolean }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_apply_catalog_product_content_batch', {
                p_admin_user_id: user.id,
                p_items: input.items,
                p_dry_run: input.dryRun,
            });
            if (error) throw new Error(error.message);
            return asPayload<CatalogBatchResult>(data);
        },
        onSuccess: (_data, vars) => {
            if (vars.dryRun) return;
            return refreshStoreAfter(qc, 'fichas-import');
        },
    });
}

export function useCatalogAllowlist(supplier?: string) {
    const { user } = useAuth();
    return useQuery<CatalogAllowlistSavedItem[]>({
        queryKey: [...QK_ALLOWLIST, supplier ?? ''],
        enabled: Boolean(user?.id && supplier),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_catalog_allowlist', {
                p_admin_user_id: user!.id,
                p_supplier: supplier,
            });
            if (error) throw new Error(error.message);
            const payload = asPayload<{ items?: CatalogAllowlistSavedItem[] }>(data);
            return payload.items ?? [];
        },
        staleTime: 15_000,
    });
}

export function useReplaceCatalogAllowlist() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: {
            supplier: string;
            items: Array<{ externalId: string; sourceUrl?: string; nombre?: string }>;
        }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc('admin_replace_catalog_allowlist', {
                p_admin_user_id: user.id,
                p_supplier: input.supplier,
                p_items: input.items,
            });
            if (error) throw new Error(error.message);
            return asPayload<{ supplier?: string; saved?: number }>(data);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK_ALLOWLIST });
            qc.invalidateQueries({ queryKey: QK_SUPPLIERS });
        },
    });
}

export function useLoadCatalogRemoteList() {
    return useMutation({
        mutationFn: async (supplier: string) => {
            const { data, error } = await apiClient.post<CatalogRemoteListResult>(
                '/store/admin/catalog-remote-list',
                { supplier },
            );
            if (error) throw new Error(error.message);
            if (!data?.ok) throw new Error('No se pudo cargar el catálogo del proveedor');
            return data;
        },
    });
}

export function useRunCatalogSync() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: {
            supplier?: string;
            imageLimit?: number;
            dryRun?: boolean;
        }) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await apiClient.post<{
                ok: boolean;
                runId: number;
                status: string;
                supplierSlug: string;
                dryRun?: boolean;
            }>(
                '/store/admin/catalog-sync',
                {
                    supplier: input.supplier || 'onnautic',
                    softDeactivate: true,
                    imageLimit: input.dryRun ? 0 : (input.imageLimit ?? 40),
                    dryRun: Boolean(input.dryRun),
                },
            );
            if (error) throw new Error(error.message);
            if (!data?.runId) throw new Error('No se recibió runId de la sync');
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK_RUNS });
        },
    });
}

export function isSupplierCatalogSyncStale(
    supplierSlug: string,
    runs: CatalogSyncRunRow[],
): boolean {
    const maxHours = catalogSyncStaleMaxHours(supplierSlug);
    const lastOk = runs.find(
        (r) =>
            r.supplierSlug === supplierSlug
            && r.status === 'ok'
            && r.productsUpserted > 0
            && r.finishedAt,
    );
    if (!lastOk?.finishedAt) return true;
    return isStaleSync(lastOk.finishedAt, maxHours);
}
